/**
 * Export to Cloud Storage API
 * Handles OAuth connections and export jobs
 */

import { get, post, del } from '@/services/shared/client'
import type {
  ExportProvider,
  GetConnectionsResponse,
  StartExportResponse,
  GetExportJobResponse,
  DisconnectResponse,
} from '@/types/export'

/**
 * Response from initiating OAuth connection
 */
interface InitiateConnectResponse {
  success: boolean
  data: {
    authUrl: string
  }
}

/**
 * Initiate OAuth connection for a cloud storage provider
 * POST /export/connect/:provider
 *
 * Returns the OAuth authorization URL to redirect the user to
 */
export async function initiateConnect(
  provider: ExportProvider,
  returnUrl: string
): Promise<InitiateConnectResponse> {
  return await post<InitiateConnectResponse>(`/export/connect/${provider}`, {
    returnUrl,
  })
}

/**
 * Get user's connected cloud accounts
 * GET /export/connections
 */
export async function getConnections(): Promise<GetConnectionsResponse> {
  return await get<GetConnectionsResponse>('/export/connections')
}

/**
 * Disconnect a cloud storage provider
 * DELETE /export/disconnect/:provider
 */
export async function disconnectProvider(
  provider: ExportProvider
): Promise<DisconnectResponse> {
  return await del<DisconnectResponse>(`/export/disconnect/${provider}`)
}

/**
 * Start an export job for a specific invitation
 * POST /invitations/:invitationId/guest-uploads/export
 * Provider defaults to google-drive on backend
 */
export async function startExport(
  invitationId: string,
  options?: { folderName?: string }
): Promise<StartExportResponse> {
  return await post<StartExportResponse>(
    `/invitations/${invitationId}/guest-uploads/export`,
    options || {}
  )
}

/**
 * Start an export job for ALL user's guest uploads
 * POST /library/user/guest-uploads/export
 * Provider defaults to google-drive on backend
 */
export async function startExportAllGuestUploads(options?: {
  folderName?: string
}): Promise<StartExportResponse> {
  return await post<StartExportResponse>(
    `/library/user/guest-uploads/export`,
    options || {}
  )
}

/**
 * Get export job status
 * GET /export/jobs/:jobId
 */
export async function getExportJob(
  jobId: string
): Promise<GetExportJobResponse> {
  return await get<GetExportJobResponse>(`/export/jobs/${jobId}`)
}

/**
 * Cancel an export job
 * DELETE /export/jobs/:jobId
 */
export async function cancelExportJob(
  jobId: string
): Promise<{ success: boolean; message: string }> {
  return await del<{ success: boolean; message: string }>(
    `/export/jobs/${jobId}`
  )
}
